<script setup>
import {defineProps, ref, inject, onBeforeMount} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import OxdLabel from '@ohrm/oxd/core/components/Label/Label.vue';
import OxdChip from '@ohrm/oxd/core/components/Chip/Chip.vue';
import {TypeOfPlanConst} from '../../constants/TypeOfPlan';
import {
  TypeOfActivityConst,
  TypeOfActivityList,
} from '../../constants/TypeOfActivity';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {formatDate} from '@/core/util/helper/datefns';

const http = new APIService(window.appGlobal.baseUrl, 'api/v2/pim/onboarding');
const $toast = inject('$toast');
const {jsDateFormat} = useDateFormat();

const props = defineProps({
  id: {
    type: Number,
    default() {
      return null;
    },
  },
  canCompleteTask: {
    type: Boolean,
  },
});

const isLoading = ref(false);
const onboarding = ref({selectedTypeOfActivitiesIds: []});

const implodeObject = (object, keyName = 'label') => {
  return object?.map(x => x[keyName]).join(', ');
};

const getOnboardingData = async () => {
  isLoading.value = true;
  await http
    .request({
      url: `api/onboarding/${props.id}`,
    })
    .then(response => {
      onboarding.value = response.data;
      onboarding.value.departments = implodeObject(
        onboarding.value.departments,
      );
      onboarding.value.subunits = implodeObject(onboarding.value.subunits);
      onboarding.value.assignedUsers = implodeObject(
        onboarding.value.assignedUsers,
      );
    })
    .catch(() => {
      $toast.unexpectedError();
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const openFile = path => {
  window.open(`${window.appGlobal.publicPath}/${path}`, '_blank');
};

const redirectTo = path => {
  window.open(path, '_blank');
};

const completeTask = async (index, stageIndex = null) => {
  onboarding.value.selectedTasks;

  let taskId = null;
  const formData = new FormData();

  if (stageIndex != null) {
    taskId =
      onboarding.value.activities[stageIndex]?.infos[index]?.activity?.id;
    formData.append('stageId', onboarding.value.activities[stageIndex]?.id);
  } else {
    taskId = onboarding.value.activities[index]?.activity?.id;
  }

  if (taskId) {
    isLoading.value = true;
    await http
      .request({
        url: `/api/onboarding/${onboarding.value?.id}/complete-task/${taskId}`,
        method: 'POST',
        data: formData,
      })
      .then(() => {
        if (stageIndex != null) {
          onboarding.value.activities[stageIndex].infos[
            index
          ].completeDate = formatDate(new Date(), jsDateFormat);
        } else {
          onboarding.value.activities[index].completeDate = formatDate(
            new Date(),
            jsDateFormat,
          );
        }
        $toast.saveSuccess();
      })
      .catch(() => {
        $toast.unexpectedError();
      })
      .finally(() => {
        isLoading.value = false;
      });
  }
};

onBeforeMount(() => {
  getOnboardingData();
});
</script>
<template>
  <div class="orangehrm-background-container">
    <oxd-form :loading="isLoading">
      <oxd-form-row>
        <oxd-grid :cols="4" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              :value="`ONB-${onboarding.id}`"
              label="ID"
              :disabled="true"
            />
          </oxd-grid-item>
          <oxd-grid-item class="--span-column-2">
            <oxd-input-field
              :value="onboarding.title"
              label="Title"
              :disabled="true"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              :value="onboarding.status?.label"
              label="Status"
              :disabled="true"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-input-field
          :value="onboarding.description"
          type="textarea"
          label="Description"
          placeholder="Description"
          :disabled="true"
        />
      </oxd-form-row>
      <oxd-form-row>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <date-input
              :value="
                onboarding.dueDate
                  ? formatDate(new Date(onboarding.dueDate), jsDateFormat)
                  : ''
              "
              label="Due Date"
              :disabled="true"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              :value="onboarding.responsible?.label"
              label="Plan Manager"
              :disabled="true"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-grid :cols="3">
          <oxd-grid-item>
            <oxd-input-field
              :value="onboarding?.departments"
              label="Departments"
              :disabled="true"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              :value="onboarding?.subunits"
              label="Sub Unit(s)"
              :disabled="true"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              :value="onboarding?.assignedUsers"
              label="Assign to"
              :disabled="true"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <!-- FILE UPLOAD SECTION -->
      <oxd-form-row>
        <oxd-table-filter filter-title="Files" style="padding-left: 0;">
          <div
            v-if="onboarding?.files?.length != 0"
            class="flex gap-3 items-center mb-2.5"
          >
            <template v-for="file in onboarding.files" :key="file.name">
              <oxd-icon-button name="download" @click="openFile(file.value)" />
              <oxd-label :label="file.name" />
            </template>
          </div>
          <div v-else><oxd-text tag="p">No records found.</oxd-text></div>
        </oxd-table-filter>
      </oxd-form-row>
      <!-- END: FILE UPLOAD SECTION -->
      <oxd-form-row>
        <oxd-text tag="h6" class="orangehrm-main-title">
          {{
            onboarding.selectedTypeOfPlan?.id == TypeOfPlanConst.SINGLE_ACTIVITY
              ? 'Activity'
              : 'Ativities'
          }}
        </oxd-text>
        <oxd-divider />
        <template
          v-if="
            onboarding.selectedTypeOfPlan?.id ==
              TypeOfPlanConst.PLAN_OF_ACTIVITIES
          "
        >
          <oxd-grid :cols="12">
            <template
              v-for="(stage, index) in onboarding.activities"
              :key="index"
            >
              <oxd-table-filter
                :filter-title="`Stage #${index + 1}`"
                :is-active="false"
                style="background: #F5F5F5; margin-bottom:18px;"
              >
                <oxd-form-row
                  v-for="(stageData, activityIndex) in stage?.infos"
                  :key="activityIndex"
                >
                  <oxd-grid :cols="6" class="orangehrm-full-width-grid">
                    <oxd-grid-item>
                      <oxd-input-field
                        label="Type of Activity"
                        :value="stageData?.typeOfActivity?.label"
                        :disabled="true"
                      />
                    </oxd-grid-item>
                    <oxd-grid-item class="--span-column-2 flex items-center">
                      <oxd-input-field
                        label="Activity"
                        :value="stageData?.activity?.label"
                        :disabled="true"
                      />
                    </oxd-grid-item>
                    <oxd-grid-item>
                      <oxd-input-field
                        label="Manager"
                        :value="stageData?.responsible?.label"
                        :disabled="true"
                      />
                    </oxd-grid-item>
                    <oxd-grid-item>
                      <oxd-input-field
                        :value="
                          stageData?.dueDate
                            ? formatDate(
                                new Date(stageData?.dueDate),
                                jsDateFormat,
                              )
                            : ''
                        "
                        label="Due Date"
                        :disabled="true"
                      />
                    </oxd-grid-item>
                    <oxd-grid-item>
                      <div
                        class="flex items-center gap-3"
                        style="margin-top:25px; min-height: 36px; "
                      >
                        <oxd-icon-button
                          v-if="stageData.redirectUri"
                          name="box-arrow-up-right"
                          class="oxd-button--secondary"
                          @click="redirectTo(stageData.redirectUri)"
                        />
                        <oxd-button
                          v-if="
                            stageData.completeDate == null &&
                              stageData.typeOfActivity.id ==
                                TypeOfActivityConst.TASK &&
                              canCompleteTask
                          "
                          style="white-space: nowrap; padding: 8px"
                          display-type="text"
                          label="Complete"
                          type="button"
                          @click="completeTask(activityIndex, index)"
                        />
                        <OxdChip
                          v-if="stageData.completeDate"
                          :label="
                            `Completed ${formatDate(
                              new Date(stageData.completeDate),
                              jsDateFormat,
                            )}`
                          "
                          style="background-color: #49c354; white-space: nowrap;"
                        />
                        <OxdChip
                          v-else
                          label="To Do"
                          style="background-color: #66D9EC; white-space: nowrap;"
                        />
                      </div>
                    </oxd-grid-item>
                  </oxd-grid>
                </oxd-form-row>
              </oxd-table-filter>
            </template>
          </oxd-grid>
        </template>
        <template v-else>
          <template
            v-for="(activity, index) in onboarding.activities"
            :key="index"
          >
            <oxd-grid :cols="2">
              <oxd-grid-item>
                <div class="flex items-center gap-3">
                  <oxd-label :label="activity.typeOfActivity?.label" />
                  <OxdChip
                    v-if="activity.completeDate"
                    :label="
                      `Completed ${formatDate(
                        new Date(activity.completeDate),
                        jsDateFormat,
                      )}`
                    "
                    style="background-color: #49c354;"
                  />
                  <OxdChip
                    v-else
                    label="To Do"
                    style="background-color: #66D9EC;"
                  />
                </div>
                <oxd-input-field
                  :value="activity?.activity?.label"
                  :disabled="true"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-icon-button
                  v-if="activity.redirectUri"
                  style="margin-top:24px"
                  name="box-arrow-up-right"
                  class="oxd-button--secondary"
                  @click="redirectTo(activity?.redirectUri)"
                />
                <oxd-button
                  v-if="
                    activity.completeDate == null &&
                      activity.typeOfActivity.id == TypeOfActivityConst.TASK &&
                      canCompleteTask
                  "
                  style="white-space: nowrap; padding: 8px; margin-top:34px;"
                  display-type="text"
                  label="Complete"
                  type="button"
                  @click="completeTask(index)"
                />
              </oxd-grid-item>
            </oxd-grid>
          </template>
        </template>
      </oxd-form-row>
    </oxd-form>
  </div>
</template>
